import { yupResolver } from '@hookform/resolvers/yup';
import { formatISO, parseISO } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import { GroupBase } from 'react-select';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import { AddFieldAIButton } from '@/modules/AiAgents/components/Agents/Nugz/Fields/AddFieldAIButton';
import {
  useCreatePropertyField,
  useGetPropertyConfigurations,
  useGetPropertyCrops,
} from '@/modules/settings/property/apiHooks';
import AddCustomCrop from '@/modules/settings/property/components/Crops/AddCustomCrop';
import { AddCustomVariety } from '@/modules/settings/property/components/Crops/AddCustomCrop/AddCustomVariety';
import CustomOperationHeader from '@/modules/settings/property/components/Jobs/AddEditCustomOperation/Header';
import { usePropertySettings } from '@/modules/settings/property/components/logic';
import { IPropertySettings } from '@/modules/settings/property/types';
import {
  useGetAllCrops,
  useGetCropVarieties,
} from '@/modules/settings/shared/apiHooks';
import { ISettings } from '@/modules/settings/shared/types';
import {
  Button,
  InputField,
  LocalizedDatePicker,
  NextImage,
  SelectField,
  Typography,
} from '@/shared/components/atoms';
import ScrollableWithShadow from '@/shared/components/atoms/ScrollableWithShadow';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import { AssetItemProps } from '@/shared/components/molecules/ChooseAsset/ChooseAsset';
import { EntityType } from '@/shared/constant/enums';
import {
  areaUnits,
  farmFields,
  farmFieldsDescriptions,
  packhouseLines,
} from '@/shared/constant/general';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { IGeneral } from '@/shared/types';

const AddPropertyField: React.FC<{
  selectedAsset: AssetItemProps | null;
  onSuccess?: () => void;
  closeOnFinish?: boolean;
}> = ({ selectedAsset, onSuccess, closeOnFinish = true }) => {
  const { t } = useTranslation(['common', 'payment']);

  const { openModal, closeModal } = useModal();

  const { createEditPropertyFieldSchema, createEditPackhouseFieldSchema } =
    usePropertySettings();

  const isFarm = selectedAsset?.entityTypeId === EntityType.FARM;

  /***** Form******/
  const formMethods = useForm<IPropertySettings.ICreateEditFieldFormValues>({
    resolver: yupResolver(
      isFarm ? createEditPropertyFieldSchema : createEditPackhouseFieldSchema
    ),
  });
  const {
    formState: { errors },
    register,
    reset,
    handleSubmit,
    watch,
  } = formMethods;

  const cropIdWatcher = watch('cropId');

  /***** API******/
  const { data: allCrops } = useGetAllCrops(selectedAsset?.id);
  const {
    data: propertyCrops,
    fetchNextPage,
    refetch: refetchCrops,
  } = useGetPropertyCrops({
    propertyId: selectedAsset?.id,
    pageNumber: 1,
    //  TODO: remove this large pageSize
    pageSize: 100000,
  });

  const {
    data: cropVarieties,
    refetch: refetchVarieties,
    isLoading: isVarietiesLoading,
  } = useGetCropVarieties(cropIdWatcher);

  const { data: configurations, refetch } = useGetPropertyConfigurations(
    selectedAsset?.id
  );
  const { mutate, isLoading } = useCreatePropertyField();

  /*****Select Fields******/
  const propertyCropsList = propertyCrops?.pages.flatMap((page) => page.items);
  const groupedOptions: GroupBase<IGeneral.ICropSchema>[] = [
    {
      label: t('recent'),
      options: propertyCropsList || [],
    },
    {
      label: t('all'),
      options:
        allCrops?.list.filter(
          (crop) =>
            !new Set(propertyCropsList?.map((item) => item.id)).has(crop.id)
        ) || [],
    },
  ];

  useEffect(() => {
    if (selectedAsset && configurations)
      formMethods.reset({
        areaUnit: configurations.areaUnit ?? 'acre',
      });
  }, [formMethods, selectedAsset, configurations]);

  const getFieldTypes = () => {
    if (selectedAsset) {
      return isFarm ? farmFields : packhouseLines;
    }
  };

  const handleCreateCustomCrop = () => {
    openModal(<AddCustomCrop onSuccessFunction={refetchCrops} />, {
      title: (
        <div>
          <Typography weight='bold'>{t('create-custom-crop')}</Typography>
          <Typography variant='body2' className='mt-4 text-surface-10'>
            {t('choose-or-create-crop')}
          </Typography>
        </div>
      ),
      containerClassName: 'min-w-[360px] !overflow-visible',
      onClose(closeModal) {
        refetchCrops();
        closeModal();
      },
    });
  };

  const getSelectedCropData = useCallback(() => {
    const cropList = allCrops?.list?.filter(
      (crop) => crop.id === cropIdWatcher
    );
    return cropList?.length ? cropList[0] : undefined;
  }, [cropIdWatcher, allCrops]);

  const handleCreateCustomVariety = () => {
    const crop = getSelectedCropData();
    openModal(
      <AddCustomVariety
        cropId={cropIdWatcher}
        onSuccessFunction={refetchVarieties}
      />,
      {
        title: (
          <CustomOperationHeader
            cropName={crop?.name ?? ''}
            cropImage={crop?.image}
            type='variety'
          />
        ),
        containerClassName: 'min-w-[360px] !overflow-visible',
        onClose(closeModal) {
          refetchVarieties();
          closeModal();
        },
      }
    );
  };

  const onSubmit: SubmitHandler<
    IPropertySettings.ICreateEditFieldFormValues
  > = (values, event) => {
    if (selectedAsset)
      mutate(
        {
          entityId: selectedAsset?.id,
          ...values,
        },
        {
          onSuccess: () => {
            const id = (event?.nativeEvent as SubmitEvent).submitter?.id;
            if (onSuccess) onSuccess();
            else toast.success(t('Your Field is created successfully'));
            if (id === 'save-and-add') {
              reset();
              refetch();
            } else if (id === 'save-and-close') {
              if (closeOnFinish) closeModal();
            }
          },
        }
      );
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='m-auto flex h-full flex-col justify-between tablet:max-w-[50%] desktop:max-w-[30%]'
      >
        <div>
          <div className='mb-24 mt-32'>
            <Typography variant='h2' weight='medium'>
              {t('Add Field')}
            </Typography>
            <Typography
              variant='body2'
              className='mt-4 max-w-sm text-surface-10'
            >
              {t('field-meaning')}
            </Typography>
            <div className='mt-24'>
              <AddFieldAIButton />
            </div>
          </div>
          <ScrollableWithShadow maxHeight='max-h-464'>
            <div className='flex flex-col gap-16'>
              <InputField
                borderColor='border-outline-variant'
                width='w-full'
                py='!py-16'
                label={t('Field Name')}
                placeholder={t('Field Name')}
                error={!!errors.name}
                errorMessage={errors.name?.message}
                className='!rounded-xl bg-background-transparent'
                disabled={isLoading}
                {...register('name', {
                  setValueAs: (value) => (value?.length ? value : undefined),
                })}
              />
              <SelectField<IGeneral.LabelValuePair, false>
                id='type'
                getLabel={(obj: IGeneral.LabelValuePair) => obj.label}
                getValue={(obj: IGeneral.LabelValuePair) => obj.value}
                isDisabled={isLoading}
                className='!rounded-xl bg-background-transparent'
                borderColor={themeColors().outline.variant}
                label={t('Field Type')}
                placeholder={t('Field Type')}
                list={getFieldTypes()?.map((item) => ({
                  label: t(item.toUpperCase()),
                  value: item,
                }))}
                py='10px'
                px='0.5rem'
                borderRadius='1rem'
                width='w-full'
                customOption={
                  isFarm
                    ? (option) => (
                        <div>
                          <Typography weight='bold'>
                            {t(option.data.label)}
                          </Typography>
                          <Typography variant='subtitle2'>
                            {t(
                              farmFieldsDescriptions[
                                option.data.value as ISettings.FarmFieldType
                              ]
                            )}
                          </Typography>
                        </div>
                      )
                    : undefined
                }
              />
              {isFarm && (
                <>
                  <SelectField
                    id='areaUnit'
                    placeholder={t('Unit')}
                    label={t('Area unit')}
                    isDisabled={!!configurations?.areaUnit}
                    menuPlacement='top'
                    getLabel={(obj: IGeneral.LabelValuePair) => obj.label}
                    getValue={(obj: IGeneral.LabelValuePair) => obj.value}
                    className='cursor-pointer !rounded-xl bg-background-transparent'
                    borderColor={themeColors().outline.variant}
                    isMulti={false}
                    list={areaUnits.map((unit) => ({
                      label: t(unit),
                      value: unit,
                    }))}
                    py='10px'
                    px='0.5rem'
                    borderRadius='1rem'
                    width='w-full'
                  />
                  <InputField
                    borderColor='border-outline-variant'
                    width='w-full'
                    py='!py-16'
                    label={t('area')}
                    placeholder={t('area')}
                    error={!!errors.area}
                    errorMessage={errors.area?.message}
                    className='!rounded-xl bg-background-transparent'
                    disabled={isLoading}
                    {...register('area', {
                      setValueAs: (value) =>
                        value?.length ? value : undefined,
                    })}
                    type='number'
                    step='0.001'
                  />
                  <InfiniteSelectField<
                    IGeneral.ICropSchema,
                    true,
                    GroupBase<IGeneral.ICropSchema>
                  >
                    isGroup
                    id='cropId'
                    getLabel={(obj: IGeneral.ICropSchema) => obj.name}
                    getValue={(obj: IGeneral.ICropSchema) => obj.id}
                    className='!rounded-xl bg-background-transparent'
                    borderColor={themeColors().outline.variant}
                    label={t('crop')}
                    placeholder={t('search-or-select-crop')}
                    isDisabled={isLoading}
                    list={allCrops?.list}
                    options={groupedOptions}
                    py='10px'
                    px='0.5rem'
                    maxMenuHeight={200}
                    borderRadius='1rem'
                    width='w-full'
                    formatOptionLabel={(option: IGeneral.ICropSchema) => (
                      <div className='flex items-center gap-12'>
                        {option.image && (
                          <NextImage
                            src={option.image}
                            width={24}
                            height={16}
                            alt='country flag'
                          />
                        )}
                        {option.name}
                      </div>
                    )}
                    fetchNextPage={fetchNextPage}
                    createOption={{
                      title: t('create-custom-crop'),
                      onClick: handleCreateCustomCrop,
                    }}
                  />
                  <SelectField
                    id='varietyId'
                    getLabel={(obj: IGeneral.ICropSchema) => obj.name}
                    getValue={(obj: IGeneral.ICropSchema) => obj.id}
                    className='!rounded-xl bg-background-transparent'
                    borderColor={themeColors().outline.variant}
                    label={`${t('Variety')} (${t('optional')})`}
                    placeholder={t('Variety')}
                    isMulti={false}
                    isDisabled={
                      isLoading || !cropIdWatcher || isVarietiesLoading
                    }
                    list={cropIdWatcher ? cropVarieties : []}
                    py='10px'
                    px='0.5rem'
                    borderRadius='1rem'
                    width='w-full'
                    createOption={{
                      title: t('create-custom-variety'),
                      onClick: handleCreateCustomVariety,
                    }}
                    maxMenuHeight={160}
                  />
                  <Controller
                    name='plantationDate'
                    control={formMethods.control}
                    render={({ field: { onChange, ...field } }) => (
                      <LocalizedDatePicker
                        inputProps={{
                          label: `${t('Plantation Date')} (${t('optional')})`,
                          error: !!formMethods.formState.errors?.plantationDate,
                          errorMessage:
                            formMethods.formState.errors?.plantationDate
                              ?.message,
                          className: '!rounded-xl bg-background-transparent',
                          borderColor: 'border-outline-variant',
                          width: 'w-full',
                          py: '!py-16',
                          disabled: isLoading,
                        }}
                        datePickerProps={{
                          placeholderText: t('Plantation Date'),
                          dateFormat: 'MMM d, yyyy',
                          selected: field?.value
                            ? parseISO(field.value)
                            : undefined,
                          onChange: (date) => {
                            if (date)
                              onChange(
                                formatISO(new Date(date), {
                                  representation: 'date',
                                })
                              );
                          },
                        }}
                      />
                    )}
                  />
                </>
              )}
            </div>
          </ScrollableWithShadow>
        </div>
        <div className='mt-32 flex justify-end gap-8'>
          <Button
            variant='text'
            size='lg'
            className='!rounded-xl border-xs border-outline-default'
            type='submit'
            id='save-and-add'
          >
            {t('save-and-add-another')}
          </Button>
          <Button type='submit' id='save-and-close'>
            {t('confirm')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default AddPropertyField;
